import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { CaseForCreation } from '../../../../core/models/case/case-for-creation.model';
import { CaseForUpdation } from '../../../../core/models/case/case-for-updation.model';
import { CaseRequestFilter } from '../../../../core/models/case/case-request-filter.model';
import { CaseTypeForListing } from '../../../../core/models/casetypes/casetype-for-listing.model';
import { CaseTypeRequestFilter } from '../../../../core/models/casetypes/casetype-request-filter.model';
import { ClientForListing } from '../../../../core/models/clients/client-for-listing.model';
import { CourtTypeForListing } from '../../../../core/models/courttypes/courttype-for-listing.model';
import { CourtTypeRequestFilter } from '../../../../core/models/courttypes/courttype-request-filter.model';
import { DistrictForListing } from '../../../../core/models/districts/district-for-listing.model';
import { DistrictRequestFilter } from '../../../../core/models/districts/district-request-filter.model';
import { CaseTypeService } from '../../../../core/services/case-type.service';
import { CaseService } from '../../../../core/services/case.service';
import { ClientService } from '../../../../core/services/client.service';
import { CourtTypeService } from '../../../../core/services/court-type.service';
import { DistrictService } from '../../../../core/services/district.service';
import { FileUploaderComponent } from '../../../../shared/components/file-uploader/file-uploader.component';
import { AttachmentForCreation } from '../../../../core/models/attachments/attachment-for-creation.model';

@Component({
  selector: 'app-case-form',
  standalone: false,

  templateUrl: './case-form.component.html',
  styleUrl: './case-form.component.css'
})
export class CaseFormComponent implements OnInit {
  caseForm!: FormGroup;
  isEditMode = false;
  isSubmited = false;
  caseId!: number;
  clientId: number | null = null;
  clients: ClientForListing[] = [];
  districts: DistrictForListing[] = [];
  courtTypes: CourtTypeForListing[] = [];
  caseTypes: CaseTypeForListing[] = [];
  @ViewChild(FileUploaderComponent) fileUploader!: FileUploaderComponent;
  private returnUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private caseService: CaseService,
    private clientService: ClientService,
    private districtService: DistrictService,
    private courtTypeService: CourtTypeService,
    private caseTypeService: CaseTypeService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.loadClients();
    this.loadDistricts();
    this.loadCourtTypes();
    this.loadCaseTypes();

    // Auto-select oppositePartyRole based on partyRole selection
    this.caseForm.get('partyRole')?.valueChanges.subscribe((partyRole: string) => {
      if (partyRole === 'Applicant') {
        this.caseForm.get('oppositePartyRole')?.setValue('Defendant');
      } else if (partyRole === 'Defendant') {
        this.caseForm.get('oppositePartyRole')?.setValue('Applicant');
      } else {
        this.caseForm.get('oppositePartyRole')?.setValue('');
      }
    });

    // Get returnUrl from router state, history state, or query params
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['returnUrl']) {
      this.returnUrl = navigation.extras.state['returnUrl'];
    } else if ((window.history.state as any)?.returnUrl) {
      this.returnUrl = (window.history.state as any).returnUrl;
    } else {
      this.route.queryParams.subscribe(params => {
        if (params['returnUrl']) {
          this.returnUrl = params['returnUrl'];
        }
      });
    }

    // Read clientId from query params (regardless of returnUrl)
    // First check synchronously from snapshot
    const queryParams = this.route.snapshot.queryParams;
    if (queryParams['clientId']) {
      this.clientId = +queryParams['clientId']; // convert to number
      this.caseForm.patchValue({ clientId: this.clientId });
    }
    
    // Also subscribe for any changes
    this.route.queryParams.subscribe(params => {
      if (params['clientId']) {
        this.clientId = +params['clientId']; // convert to number
        this.caseForm.patchValue({ clientId: this.clientId });
      }
    });

    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = true;
        this.caseId = +params['id'];
        this.loadCaseData(this.caseId);
      }
    });
  }

  private navigateBack(): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      // If we have a clientId, try to go to client details, otherwise use history or default
      if (this.clientId) {
        this.router.navigate(['/clients/details', this.clientId], {
          queryParams: { activeTab: 'cases' }
        });
      } else if (window.history.length > 1) {
        this.location.back();
      } else {
        this.router.navigate(['/cases']);
      }
    }
  }

  private buildForm(): void {
    this.caseForm = this.fb.group({
      caseNumber: [''],
      year: [null],
      filingDate: [null],
      districtId: [null, Validators.required],
      courtTypeId: [null],
      caseTypeId: [null],
      clientId: [null, Validators.required],
      underSection: [''],
      status: ['', Validators.required],
      partyRole: ['', Validators.required],
      judge: [''],
      oppositeParty: [''],
      oppositePartyAdvocate: [''],
      oppositePartyRole: ['', Validators.required],
      transferredAt: [null],
      disposedAt: [null]
    });
  }

  private loadClients(): void {

    const filter: CaseRequestFilter = {
      page: 1,
      pageSize: 100000
    };

    this.clientService.getPaginatedClients(filter).subscribe({
      next: (res) => this.clients = res.data,
      error: () => this.snackBar.open('Failed to load clients', 'Close', { duration: 3000 })
    });
  }
  private loadDistricts(): void {
    const filter: DistrictRequestFilter = {
      page: 1,
      pageSize: 100000
    };

    this.districtService.getPaginatedDistricts(filter).subscribe({
      next: (res) => this.districts = res.data,
      error: () => this.snackBar.open('Failed to load districts', 'Close', { duration: 3000 })
    });
  }
  private loadCourtTypes(): void {
    const filter: CourtTypeRequestFilter = {
      page: 1,
      pageSize: 100000
    };

    this.courtTypeService.getPaginatedCourtTypes(filter).subscribe({
      next: (res) => this.courtTypes = res.data,
      error: () => this.snackBar.open('Failed to load court types', 'Close', { duration: 3000 })
    });
  }
  private loadCaseTypes(): void {
    const filter: CaseTypeRequestFilter = {
      page: 1,
      pageSize: 100000
    };

    this.caseTypeService.getPaginatedCaseTypes(filter).subscribe({
      next: (res) => this.caseTypes = res.data,
      error: () => this.snackBar.open('Failed to load case types', 'Close', { duration: 3000 })
    });
  }

  private loadCaseData(id: number): void {
    this.caseService.getCaseById(id).subscribe({
      next: (caseData) => this.caseForm.patchValue(caseData),
      error: () => this.snackBar.open('Failed to load case details', 'Close', { duration: 3000 })
    });
  }

  async submitForm(): Promise<void> {
    if (this.caseForm.invalid) {
      this.isSubmited = false;
      return;
    }
    // helper: convert date to yyyy-MM-dd if exists (using local timezone to prevent date shift)
    // Note: DateSerializerInterceptor handles Date objects automatically, but this ensures
    // string conversion if dates are already strings or need explicit formatting
    const normalizeDates = (obj: any) => {
      ['transferredAt', 'disposedAt'].forEach(key => {
        if (obj[key]) {
          const d = obj[key] instanceof Date ? obj[key] : new Date(obj[key]);
          // Use local timezone methods to prevent date shift (same as interceptor)
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          obj[key] = `${year}-${month}-${day}`;
        }
      });
      return obj;
    };

    if (this.isEditMode) {
      let updatedCase: CaseForUpdation = {
        id: this.caseId,
        ...this.caseForm.value
      };
      updatedCase = normalizeDates(updatedCase);
      this.caseService.updateCase(updatedCase).subscribe({
        next: () => {
          this.snackBar.open('Case updated successfully', 'Close', { duration: 3000 });
          this.navigateBack();
        },
        error: () => {
          this.snackBar.open('Failed to update case', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });

    } else {
      // Upload files before submitting diary
      const uploadedFiles = await this.fileUploader.uploadAllFiles();
      const attachments: AttachmentForCreation[] = uploadedFiles.map(file => ({
        blobId: file.id,
        attachmentType: 'Case'
      }));

      let newCase: CaseForCreation = this.caseForm.value;
      newCase.attachments = attachments;

      newCase = normalizeDates(newCase);
      this.caseService.createCase(newCase).subscribe({
        next: () => {
          this.snackBar.open('Case added successfully', 'Close', { duration: 3000 });
          this.navigateBack();
        },
        error: () => {
          this.snackBar.open('Failed to add case', 'Close', { duration: 3000 });
          this.isSubmited = false;
        }
      });
    }
  }
}
